<template>
  <div class="relay-ecology-panel" @click.self="unlockActive">
    <div class="rep-header">
      <div class="rep-header__left">
        <div class="rep-title">
          接力生态联动看板
          <el-tooltip
            content="观察生态趋势、隔夜反馈与晋级能力，辅助判断当前接力土壤。"
            placement="top"
          >
            <i class="el-icon-question rep-title__help" />
          </el-tooltip>
        </div>
        <div class="rep-subtitle">
          观察生态趋势、隔夜反馈与晋级能力，辅助判断当前接力土壤
          <span v-if="resolvedDate" class="rep-resolved">最新可用：{{ resolvedDate }}</span>
        </div>
      </div>
      <div class="rep-badges">
        <span class="rep-badge rep-badge--normal">NORMAL</span>
        <button type="button" class="rep-badge rep-badge--s" @click.stop="jumpSoil('S')">
          S × {{ windowCounts.s }}
        </button>
        <button type="button" class="rep-badge rep-badge--splus" @click.stop="jumpSoil('S_PLUS')">
          S+ × {{ windowCounts.sPlus }}
        </button>
      </div>
    </div>

    <div v-if="loadError" class="rep-error">{{ loadError }}</div>

    <div v-else class="rep-body">
      <div class="rep-side">
        <!-- LEVEL 1–4 当前土壤 -->
        <div class="soil-card" :class="'soil-card--' + activeSoil.tone">
          <div class="soil-card__title">
            <span>当前土壤：</span>
            <b class="soil-mark" :class="'soil-mark--' + activeSoil.tone">{{ activeSoil.mark }}</b>
            <span class="soil-card__suffix">｜{{ activeSoil.suffix }}</span>
            <i class="el-icon-arrow-right soil-card__chev" />
          </div>
          <div class="soil-card__date">
            {{ activePoint.date }}
            <span v-if="activeWeekday" class="soil-card__weekday">{{ activeWeekday }}</span>
          </div>
          <div class="soil-card__desc">{{ soilBrief }}</div>
        </div>

        <!-- S/S+ 次日 1→2 / 2→3 高板历史结果（侧栏定高，名单卡内滚动） -->
        <div v-if="showNextDayPanel" class="nextday-card">
          <div class="nextday-card__head">
            <span>{{ nextDayTitle }}</span>
            <el-tooltip
              content="★高板为历史结果标记，不属于当日可用交易信息。资金净流入悬停查看，仅用于复盘，不参与当日决策。"
              placement="top"
            >
              <i class="el-icon-warning-outline nextday-card__help" />
            </el-tooltip>
          </div>
          <div class="nextday-card__src">
            {{ resultSoilPoint.date }}｜{{ resultSoilLabel }}
            <template v-if="nextDayResult && nextDayResult.actionDate">
              → {{ nextDayResult.actionDate }}
            </template>
          </div>

          <div class="nextday-card__scroll">
            <div v-if="nextDayLoading" class="nextday-card__loading">加载中…</div>
            <div v-else-if="nextDayError" class="nextday-card__error">{{ nextDayError }}</div>
            <template v-else-if="nextDayResult && nextDayResult.eligible && nextDayResult.actionDate">
              <div class="nextday-stage">
                <div class="nextday-stage__title">
                  【1→2】
                  <span class="nextday-stage__counts">
                    通过 {{ nextDayResult.stage12.v2aPassN }}｜5+ {{ nextDayResult.stage12.final5PlusN }}
                  </span>
                </div>
                <div v-if="stage12Winners.length" class="nextday-winners">
                  <div
                    v-for="s in stage12Winners"
                    :key="'12-' + s.code"
                    class="nextday-winner"
                    :title="nciHoverTip(s)"
                  >
                    <span class="nextday-winner__name">{{ s.name || s.code }}</span>
                    <span class="nextday-winner__code">{{ s.code }}</span>
                    <span class="nextday-winner__star">★{{ s.finalBoards }}</span>
                  </div>
                </div>
                <div v-else class="nextday-empty">暂无5+高板</div>
              </div>

              <div class="nextday-stage">
                <div class="nextday-stage__title">
                  【2→3】
                  <span class="nextday-stage__counts">
                    通过 {{ nextDayResult.stage23.v2aPassN }}｜5+ {{ nextDayResult.stage23.final5PlusN }}
                  </span>
                </div>
                <div v-if="stage23Winners.length" class="nextday-winners">
                  <div
                    v-for="s in stage23Winners"
                    :key="'23-' + s.code"
                    class="nextday-winner"
                    :title="nciHoverTip(s)"
                  >
                    <span class="nextday-winner__name">{{ s.name || s.code }}</span>
                    <span class="nextday-winner__code">{{ s.code }}</span>
                    <span class="nextday-winner__star">★{{ s.finalBoards }}</span>
                  </div>
                </div>
                <div v-else class="nextday-empty">暂无5+高板</div>
              </div>
            </template>
            <div v-else-if="nextDayResult && !nextDayResult.eligible" class="nextday-empty">
              该日期无 S / S+ 土壤信号
            </div>
          </div>
        </div>
        <div v-else-if="resultSoilPoint && resultSoilPoint.soilState === 'NORMAL'" class="nextday-hint">
          该日期无 S / S+ 土壤信号
        </div>

        <button v-if="locked" type="button" class="unlock-btn" @click.stop="unlockActive">
          恢复最新可用日
        </button>
      </div>

      <div class="rep-main">
        <div class="rep-legend">
          <span class="rep-legend__item"><i class="lg lg--score" />接力生态分</span>
          <span class="rep-legend__item"><i class="lg lg--prem" />隔夜溢价</span>
          <span class="rep-legend__item"><i class="lg lg--promo" />综合晋级率</span>
          <span class="rep-legend__item"><i class="lg lg--node" />节点达标</span>
        </div>

        <div ref="chartEl" class="rep-chart" @mouseleave="onChartLeave" />

        <div class="soil-track">
          <div class="soil-track__line" />
          <div
            v-for="(p, i) in series"
            :key="p.date"
            class="soil-track__cell"
            :class="{
              'is-first': i === 0,
              'is-last': i === series.length - 1
            }"
            :style="trackCellStyle(i)"
            @mouseenter="onTrackEnter(i)"
            @click.stop="lockIndex(i)"
          >
            <span
              class="soil-dot"
              :class="[
                'soil-dot--' + soilTone(p.soilState),
                { 'is-active': i === activeIndex, 'is-signal': p.soilState !== 'NORMAL' }
              ]"
            >
              <template v-if="p.soilState === 'S_PLUS'">S+</template>
              <template v-else-if="p.soilState === 'S'">S</template>
            </span>
            <span
              v-if="dateLabelIndexSet.has(i)"
              class="soil-track__date"
              :class="{
                'is-active': i === activeIndex,
                'is-resolved': p.date === resolvedDate,
                'is-stagger': dateLabelStaggerSet.has(i)
              }"
            >{{ formatMd(p.date) }}</span>
          </div>
        </div>

        <div class="rhythm-bar">
          <el-tooltip :content="lastSPlusTip" placement="top">
            <div class="rhythm-item">
              <i class="el-icon-time" />
              <span class="rhythm-item__label">距上次 S+</span>
              <span class="rhythm-item__value">{{ daysSinceText }}</span>
            </div>
          </el-tooltip>
          <el-tooltip :content="avgIntervalTip" placement="top">
            <div class="rhythm-item">
              <i class="el-icon-s-data" />
              <span class="rhythm-item__label">S+ 均值</span>
              <span class="rhythm-item__value">{{ avgIntervalText }}</span>
            </div>
          </el-tooltip>
          <div class="rhythm-item">
            <i class="el-icon-refresh" />
            <span class="rhythm-item__label">近{{ series.length }}D</span>
            <span class="rhythm-item__value">
              <em class="rhythm-s">S:{{ rhythm.recentSCount }}</em>
              <em class="rhythm-splus">S+:{{ rhythm.recentSPlusCount }}</em>
            </span>
          </div>
          <el-popover
            placement="top-end"
            trigger="hover"
            :open-delay="100"
            :close-delay="80"
            popper-class="relay-rhythm-hist-popper"
          >
            <div class="rhythm-hist-pop">
              <div class="hist-card">
                <div class="hist-card__head">历史参考｜2→3</div>
                <template v-if="researchBlock">
                  <div class="hist-card__metrics">
                    <div class="hist-metric">
                      <span class="hist-metric__label">综合晋级率</span>
                      <b class="hist-metric__val">{{ pct(researchBlock.pass) }}</b>
                    </div>
                    <div class="hist-metric">
                      <span class="hist-metric__label">NORMAL</span>
                      <b class="hist-metric__val">{{ pct(researchRef.normalPass23) }}</b>
                    </div>
                    <div class="hist-metric">
                      <span class="hist-metric__label">Lift</span>
                      <b class="hist-metric__val hist-metric__val--lift">+{{ pp(researchBlock.lift) }}</b>
                    </div>
                  </div>
                  <div v-if="researchBlock.extra" class="hist-card__extra">{{ researchBlock.extra }}</div>
                </template>
                <template v-else>
                  <div class="hist-card__metrics hist-card__metrics--single">
                    <div class="hist-metric">
                      <span class="hist-metric__label">NORMAL</span>
                      <b class="hist-metric__val">{{ pct(researchRef.normalPass23) }}</b>
                    </div>
                  </div>
                </template>
              </div>
              <div class="aux-box">
                <div class="aux-box__head">其他指标</div>
                <div class="aux-box__row">
                  <i class="el-icon-data-analysis" />
                  <span class="aux-box__label">分歧解决率</span>
                  <b class="aux-box__val">{{ pct(activePoint.resolutionRate) }}</b>
                </div>
              </div>
            </div>
            <button slot="reference" type="button" class="rhythm-link" @click.stop>
              <i class="el-icon-date" />
              历史节奏参考
            </button>
          </el-popover>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { stockApi } from '@/api/stockApi'
import { getSoilDisplay } from '../mock/relayEcologyMock'

const GRID = { left: 36, right: 36, top: 14, bottom: 4 }

const FALLBACK_REF = {
  normalPass23: 0.2,
  sPass23: 0.287,
  sPlusPass23: 0.294,
  sSealOpenPosSurvivor: 0.561,
  sSealOpenGe5Survivor: 0.348,
  sPlusNextStageSurvivor: 0.423,
  normalNextStageSurvivor: 0.344
}

function pct1(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—'
  return `${(Number(x) * 100).toFixed(1)}%`
}

function fmtPrem(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—'
  return `${Number(x).toFixed(1)}%`
}

function fmtScore(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—'
  return Number(x).toFixed(1)
}

function rateToPctNum(x) {
  if (x == null || !Number.isFinite(Number(x))) return null
  return Number(x) * 100
}

function median(arr) {
  const a = arr.filter((x) => x != null && Number.isFinite(x)).slice().sort((x, y) => x - y)
  if (!a.length) return null
  const m = Math.floor(a.length / 2)
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2
}

const WEEKDAY_CN = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

function weekdayCn(ymd) {
  if (!ymd || String(ymd).length < 10) return ''
  const d = new Date(`${String(ymd).slice(0, 10).replace(/-/g, '/')} 12:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  return WEEKDAY_CN[d.getDay()]
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function isNodeHit(p) {
  if (!p) return false
  if (p.greedyNodeHit === true) return true
  const s = Number(p.greedyNodeScore)
  return Number.isFinite(s) && s >= 7
}

export default {
  name: 'RelayEcologyPanel',
  props: {
    selectedDate: { type: String, default: '' }
  },
  data() {
    return {
      series: [],
      resolvedDate: '',
      researchRef: { ...FALLBACK_REF },
      soilHistory: [],
      loadError: '',
      loading: false,
      hoverIndex: null,
      lockedIndex: null,
      chart: null,
      resizeObs: null,
      dataMode: 'REAL',
      nextDayResult: null,
      nextDayLoading: false,
      nextDayError: '',
      nextDayFetchToken: 0
    }
  },
  computed: {
    defaultIndex() {
      if (!this.series.length) return -1
      if (this.resolvedDate) {
        const i = this.series.findIndex((p) => p.date === this.resolvedDate)
        if (i >= 0) return i
      }
      return this.series.length - 1
    },
    locked() {
      return this.lockedIndex != null
    },
    activeIndex() {
      if (this.lockedIndex != null) return this.lockedIndex
      if (this.hoverIndex != null) return this.hoverIndex
      return this.defaultIndex
    },
    activePoint() {
      return (
        this.series[this.activeIndex] || {
          date: '—',
          soilState: 'NORMAL',
          soilDesc: '暂无数据',
          rewardLevel: null,
          supportLevel: null,
          iceFlush: false,
          ecologyScore: null,
          nextOpenPremium: null,
          compositePromotionRate: null,
          resolutionRate: null,
          feature: null
        }
      )
    },
    activeSoil() {
      const p = this.activePoint
      const base = getSoilDisplay(p.soilState, p.soilLabel)
      if (p.soilState === 'S_PLUS') {
        return {
          ...base,
          mark: 'S+',
          suffix: '冰点修复',
          shortTitle: 'S+｜冰点修复',
          title: 'S+｜冰点修复',
          tone: 'splus'
        }
      }
      if (p.soilState === 'S') {
        return {
          ...base,
          mark: 'S',
          suffix: '主升土壤',
          shortTitle: 'S｜主升土壤',
          title: 'S｜主升土壤',
          tone: 's'
        }
      }
      return {
        ...base,
        mark: 'NORMAL',
        suffix: '普通土壤',
        shortTitle: 'NORMAL｜普通土壤',
        title: 'NORMAL｜普通土壤',
        tone: 'normal'
      }
    },
    activeWeekday() {
      return weekdayCn(this.activePoint.date)
    },
    soilBrief() {
      const st = this.activePoint.soilState
      if (st === 'S_PLUS') return '冰点修复环境，接力链重新打开。'
      if (st === 'S') return '主升环境，接力与赚钱效应较强。'
      return '普通接力环境，无特殊土壤信号。'
    },
    researchBlock() {
      const p = this.activePoint
      const r = this.researchRef
      if (p.soilState === 'S') {
        return {
          pass: r.sPass23,
          lift: r.sPass23 - r.normalPass23,
          feature: null,
          extra: `封板后正溢价 ${pct1(r.sSealOpenPosSurvivor)}`
        }
      }
      if (p.soilState === 'S_PLUS') {
        return {
          pass: r.sPlusPass23,
          lift: r.sPlusPass23 - r.normalPass23,
          feature: null,
          extra: `封板后再晋级 ${pct1(r.sPlusNextStageSurvivor)}`
        }
      }
      return null
    },
    rhythm() {
      return this.calcRhythmAt(this.activeIndex)
    },
    windowCounts() {
      let s = 0
      let sPlus = 0
      for (const p of this.series) {
        if (p.soilState === 'S') s++
        if (p.soilState === 'S_PLUS') sPlus++
      }
      return { s, sPlus }
    },
    daysSinceText() {
      const d = this.rhythm.daysSinceLastSPlus
      if (d == null) return '—'
      return `${d}D`
    },
    avgIntervalText() {
      const a = this.rhythm.avgSPlusInterval
      if (a == null) return '—'
      return `${a}D`
    },
    lastSPlusTip() {
      const d = this.rhythm.lastSPlusDate
      return d ? `最近S+：${d}` : '截至当前日尚无 S+ 事件'
    },
    avgIntervalTip() {
      const m = this.rhythm.medianSPlusInterval
      return m == null ? '历史节奏参考（仅用当前日及以前事件）' : `S+中位间隔：${m}D（历史节奏参考）`
    },
    /**
     * 日期标签：约 7–10 个。优先贴在 S/S+ 日，再补首尾/当前日与均匀点，
     * 让土壤点与日期尽量对齐，而不是按固定步长抽到「空日」。
     */
    dateLabelIndexSet() {
      const n = this.series.length
      const out = new Set()
      if (!n) return out
      const MIN = 7
      const MAX = 10
      const target = Math.min(MAX, Math.max(MIN, Math.min(n, MAX)))

      const add = (i) => {
        if (i >= 0 && i < n) out.add(i)
      }
      add(0)
      add(n - 1)
      if (this.activeIndex != null && this.activeIndex >= 0) add(this.activeIndex)

      const signals = []
      this.series.forEach((p, i) => {
        if (p.soilState === 'S' || p.soilState === 'S_PLUS') signals.push(i)
      })

      // 信号日优先；过多则均匀抽样，保证首尾信号尽量留下
      if (signals.length && out.size + signals.length <= MAX) {
        signals.forEach(add)
      } else if (signals.length) {
        const room = Math.max(0, MAX - out.size)
        if (room > 0) {
          if (signals.length <= room) {
            signals.forEach(add)
          } else {
            for (let k = 0; k < room; k++) {
              const idx = Math.round((k * (signals.length - 1)) / Math.max(1, room - 1))
              add(signals[idx])
            }
          }
        }
      }

      // 不足 target 时按均匀网格补齐（跳过已有点）
      if (out.size < target && n > 1) {
        const slots = target
        for (let k = 0; k < slots && out.size < target; k++) {
          add(Math.round((k * (n - 1)) / Math.max(1, slots - 1)))
        }
      }

      // 仍不足则扫一遍补满到 MIN（样本很短时）
      if (out.size < Math.min(MIN, n)) {
        for (let i = 0; i < n && out.size < Math.min(MIN, n); i++) add(i)
      }

      return out
    },
    /** 相邻两格都标日期时，后一格下移一行，避免 MM-DD 叠字 */
    dateLabelStaggerSet() {
      const labeled = [...this.dateLabelIndexSet].sort((a, b) => a - b)
      const stagger = new Set()
      for (let k = 1; k < labeled.length; k++) {
        if (labeled[k] - labeled[k - 1] === 1) {
          // 连续段内交替下移
          const prevStagger = stagger.has(labeled[k - 1])
          if (!prevStagger) stagger.add(labeled[k])
        }
      }
      return stagger
    },
    showNextDayPanel() {
      const p = this.resultSoilPoint
      return !!(p && (p.soilState === 'S' || p.soilState === 'S_PLUS'))
    },
    resultSoilPoint() {
      if (this.lockedIndex != null) return this.series[this.lockedIndex] || null
      return this.series[this.defaultIndex] || null
    },
    resultSoilLabel() {
      const p = this.resultSoilPoint
      if (!p) return ''
      if (p.soilState === 'S_PLUS') return 'S+｜冰点修复'
      if (p.soilState === 'S') return 'S｜主升土壤'
      return 'NORMAL｜普通土壤'
    },
    nextDayTitle() {
      const p = this.resultSoilPoint
      const mark = p && p.soilState === 'S_PLUS' ? 'S+' : 'S'
      const d = this.nextDayResult && this.nextDayResult.actionDate
      return d ? `${mark} 次日接力结果 ${d}` : `${mark} 次日接力结果`
    },
    stage12Winners() {
      const st = this.nextDayResult && this.nextDayResult.stage12
      return (st && Array.isArray(st.winners) ? st.winners : []) || []
    },
    stage23Winners() {
      const st = this.nextDayResult && this.nextDayResult.stage23
      return (st && Array.isArray(st.winners) ? st.winners : []) || []
    }
  },
  watch: {
    selectedDate() {
      this.fetchCurve()
    },
    activeIndex(i) {
      this.syncChartHighlight(i)
    },
    lockedIndex() {
      const p = this.resultSoilPoint
      if (p) this.onActiveSoilChange(p.date, p.soilState)
      else this.onActiveSoilChange(null, null)
    },
    series() {
      const p = this.resultSoilPoint
      if (p) this.onActiveSoilChange(p.date, p.soilState)
    }
  },
  mounted() {
    this.fetchCurve()
    this.$nextTick(() => {
      this.initChart()
      if (typeof ResizeObserver !== 'undefined' && this.$refs.chartEl) {
        this.resizeObs = new ResizeObserver(() => {
          if (this.chart) this.chart.resize()
        })
        this.resizeObs.observe(this.$refs.chartEl)
      }
      window.addEventListener('resize', this.onResize)
    })
  },
  deactivated() {
    this.hideChartTip()
  },
  beforeDestroy() {
    this.hideChartTip()
    window.removeEventListener('resize', this.onResize)
    if (this.resizeObs) this.resizeObs.disconnect()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    pct(x) {
      return pct1(x)
    },
    pp(x) {
      if (x == null || !Number.isFinite(Number(x))) return '—'
      return `${(Number(x) * 100).toFixed(1)}pp`
    },
    soilTone(state) {
      if (state === 'S_PLUS') return 'splus'
      if (state === 'S') return 's'
      return 'normal'
    },
    formatMd(date) {
      if (!date || date.length < 10) return date
      return date.slice(5)
    },
    weekdayOf(date) {
      return weekdayCn(date)
    },
    /**
     * 与 ECharts category + boundaryGap:false 同坐标：
     * 第 i 点落在绘图区 i/(n-1) 处；首位/末位贴边，中间格以点为中心。
     */
    trackCellStyle(i) {
      const n = this.series.length
      const padL = GRID.left
      const padR = GRID.right
      const plot = `100% - ${padL + padR}px`
      if (n <= 1) {
        return {
          left: `${padL}px`,
          width: `calc(${plot})`
        }
      }
      const leftFrac = i === 0 ? 0 : (i - 0.5) / (n - 1)
      const rightFrac = i === n - 1 ? 1 : (i + 0.5) / (n - 1)
      return {
        left: `calc(${padL}px + (${plot}) * ${leftFrac})`,
        width: `calc((${plot}) * ${rightFrac - leftFrac})`
      }
    },
    calcRhythmAt(activeIdx) {
      if (activeIdx < 0 || !this.series.length) {
        return {
          lastSPlusDate: null,
          daysSinceLastSPlus: null,
          avgSPlusInterval: null,
          medianSPlusInterval: null,
          recentSCount: 0,
          recentSPlusCount: 0
        }
      }
      const asOf = this.series[activeIdx].date
      const hist = (this.soilHistory || []).filter((p) => p.date <= asOf)
      const cal = hist.map((p) => p.date)
      const sPlusIdx = []
      hist.forEach((p, i) => {
        if (p.soilState === 'S_PLUS') sPlusIdx.push(i)
      })
      let lastSPlusDate = null
      let daysSinceLastSPlus = null
      if (sPlusIdx.length) {
        const last = sPlusIdx[sPlusIdx.length - 1]
        lastSPlusDate = hist[last].date
        const asOfIdx = cal.indexOf(asOf)
        if (asOfIdx >= 0) daysSinceLastSPlus = asOfIdx - last
      }
      const gaps = []
      for (let i = 1; i < sPlusIdx.length; i++) gaps.push(sPlusIdx[i] - sPlusIdx[i - 1])
      const avg = gaps.length ? gaps.reduce((s, x) => s + x, 0) / gaps.length : null
      const recentSCount = this.series.filter((p, i) => i <= activeIdx && p.soilState === 'S').length
      const recentSPlusCount = this.series.filter((p, i) => i <= activeIdx && p.soilState === 'S_PLUS').length
      return {
        lastSPlusDate,
        daysSinceLastSPlus,
        avgSPlusInterval: avg == null ? null : Math.round(avg * 10) / 10,
        medianSPlusInterval: median(gaps) == null ? null : Math.round(median(gaps) * 10) / 10,
        recentSCount,
        recentSPlusCount
      }
    },
    async fetchCurve() {
      this.loading = true
      this.loadError = ''
      this.hoverIndex = null
      this.lockedIndex = null
      try {
        const data = await stockApi.getRelayEcologyCurve(this.selectedDate, 20)
        this.series = Array.isArray(data.series) ? data.series : []
        this.resolvedDate = data.resolvedDate || ''
        this.researchRef = { ...FALLBACK_REF, ...(data.researchRef || {}) }
        this.soilHistory = Array.isArray(data.soilHistory) ? data.soilHistory : []
        this.dataMode = 'REAL'
        this.$nextTick(() => this.renderChart())
      } catch (e) {
        this.series = []
        this.resolvedDate = ''
        this.loadError = (e && e.message) || '接力生态数据加载失败'
        this.dataMode = 'REAL'
        if (this.chart) this.chart.clear()
      } finally {
        this.loading = false
      }
    },
    onTrackEnter(i) {
      if (this.locked) return
      this.hoverIndex = i
    },
    lockIndex(i) {
      this.lockedIndex = i
      this.hoverIndex = null
      const p = this.series[i]
      if (p) this.onActiveSoilChange(p.date, p.soilState)
    },
    unlockActive() {
      this.lockedIndex = null
      this.hoverIndex = null
      const p = this.series[this.defaultIndex]
      if (p) this.onActiveSoilChange(p.date, p.soilState)
      else this.onActiveSoilChange(null, null)
    },
    finalBoardsTip(s) {
      if (!s || s.finalBoards == null) return '历史结果：最终连板高度未知'
      return `历史结果：该事件最终连板高度达到${s.finalBoards}板，仅用于历史复盘，不参与当日决策。`
    },
    nci2d(s) {
      if (!s || !s.nci) return '缺 缺'
      return s.nci.twoDayDisplay || `${s.nci.tMinus1?.sign || '缺'} ${s.nci.t0?.sign || '缺'}`
    },
    nci3d(s) {
      if (!s || !s.nci) return '缺 缺 缺'
      return (
        s.nci.threeDayDisplay ||
        `${s.nci.tMinus2?.sign || '缺'} ${s.nci.tMinus1?.sign || '缺'} ${s.nci.t0?.sign || '缺'}`
      )
    },
    nciHoverTip(s) {
      if (!s || !s.nci) return this.finalBoardsTip(s)
      const cells = [s.nci.tMinus2, s.nci.tMinus1, s.nci.t0].filter(Boolean)
      const lines = cells.map((c) => {
        const md = c.date ? String(c.date).slice(5) : '—'
        const amt = c.valueFmt != null ? c.valueFmt : c.sign === '缺' ? '缺' : String(c.sign)
        return `${md}  ${amt}`
      })
      return `${this.finalBoardsTip(s)}\n资金（日终）：\n${lines.join('\n')}`
    },
    onActiveSoilChange(date, soilState) {
      if (!date || date === '—') {
        this.nextDayResult = null
        this.nextDayError = ''
        return
      }
      if (soilState !== 'S' && soilState !== 'S_PLUS') {
        this.nextDayResult = null
        this.nextDayError = ''
        this.nextDayLoading = false
        return
      }
      this.fetchNextDayResults(date)
    },
    async fetchNextDayResults(soilDate) {
      const token = ++this.nextDayFetchToken
      this.nextDayLoading = true
      this.nextDayError = ''
      try {
        const data = await stockApi.getRelayEcologyNextDayStageResults(soilDate)
        if (token !== this.nextDayFetchToken) return
        this.nextDayResult = data || null
      } catch (e) {
        if (token !== this.nextDayFetchToken) return
        this.nextDayResult = null
        this.nextDayError = (e && e.message) || '次日接力结果加载失败'
      } finally {
        if (token === this.nextDayFetchToken) this.nextDayLoading = false
      }
    },
    chartHostVisible() {
      const el = this.$el
      if (!el || typeof el.getBoundingClientRect !== 'function') return false
      const rect = el.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0
    },
    hideChartTip() {
      if (this.chart) this.chart.dispatchAction({ type: 'hideTip' })
    },
    onChartLeave() {
      if (this.locked) return
      this.hoverIndex = null
      this.hideChartTip()
    },
    jumpSoil(state) {
      for (let i = this.series.length - 1; i >= 0; i--) {
        if (this.series[i].soilState === state) {
          this.lockIndex(i)
          return
        }
      }
      this.$message.info(state === 'S_PLUS' ? '当前窗口无 S+ 事件' : '当前窗口无 S 事件')
    },
    onResize() {
      if (this.chart) this.chart.resize()
    },
    initChart() {
      const el = this.$refs.chartEl
      if (!el) return
      this.chart = echarts.init(el)
      this.chart.on('updateAxisPointer', (ev) => {
        if (this.locked) return
        const axis = (ev.axesInfo || [])[0]
        if (axis && axis.value != null) {
          const idx = this.series.findIndex((p) => p.date === axis.value)
          if (idx >= 0) this.hoverIndex = idx
        }
      })
    },
    syncChartHighlight(i) {
      if (!this.chart || i < 0 || !this.series[i]) return
      if (!this.chartHostVisible()) {
        this.hideChartTip()
        return
      }
      this.chart.dispatchAction({ type: 'showTip', seriesIndex: 1, dataIndex: i })
    },
    soilBandSeries(yMax) {
      return {
        name: '_soil_band',
        type: 'bar',
        data: this.series.map((p) => {
          if (p.soilState === 'S_PLUS' || p.soilState === 'S') return yMax
          return null
        }),
        barWidth: '50%',
        barGap: '-100%',
        z: 0,
        silent: true,
        tooltip: { show: false },
        itemStyle: {
          color: (params) => {
            const p = this.series[params.dataIndex]
            if (!p) return 'transparent'
            return p.soilState === 'S_PLUS' ? 'rgba(245,108,108,0.10)' : 'rgba(230,162,60,0.10)'
          }
        }
      }
    },
    renderChart() {
      if (!this.chart || !this.series.length) return
      const dates = this.series.map((p) => p.date)
      const scores = this.series.map((p) => p.ecologyScore)
      const premiums = this.series.map((p) =>
        p.nextOpenStatus === 'UNAVAILABLE' || p.nextOpenStatus === 'MISSING' ? null : p.nextOpenPremium
      )
      const promos = this.series.map((p) => rateToPctNum(p.compositePromotionRate))
      const scoreVals = scores.filter((x) => x != null && Number.isFinite(x))
      let yMin = 0
      let yMax = 80
      if (scoreVals.length) {
        const mn = Math.min(...scoreVals)
        const mx = Math.max(...scoreVals)
        const pad = Math.max(4, (mx - mn) * 0.15)
        yMin = Math.floor(mn - pad)
        yMax = Math.ceil(mx + pad)
      }
      // 达标日在生态分线略上方画星；未达标为 null
      const nodeStarPlot = this.series.map((p) => {
        if (!isNodeHit(p)) return null
        const y = Number(p.ecologyScore)
        if (!Number.isFinite(y)) return yMin + (yMax - yMin) * 0.85
        return Math.min(yMax - 0.5, y + Math.max(1.2, (yMax - yMin) * 0.04))
      })
      const self = this

      this.chart.setOption(
        {
          animationDuration: 220,
          grid: { ...GRID, containLabel: false },
          tooltip: {
            trigger: 'axis',
            appendToBody: true,
            confine: false,
            enterable: true,
            // 优先展示在触点下方，避免顶到看板标题被裁切
            position(point, _params, _dom, _rect, size) {
              const gapX = 14
              const gapY = 22
              const tw = (size && size.contentSize && size.contentSize[0]) || 180
              const th = (size && size.contentSize && size.contentSize[1]) || 120
              const vw = (size && size.viewSize && size.viewSize[0]) || 0
              const vh = (size && size.viewSize && size.viewSize[1]) || 0
              let left = point[0] + gapX
              let top = point[1] + gapY
              if (vw && left + tw > vw - 4) left = Math.max(4, point[0] - tw - gapX)
              if (vh && top + th > vh - 4) top = Math.max(4, point[1] - th - 8)
              return [left, top]
            },
            axisPointer: {
              type: 'line',
              lineStyle: { color: 'rgba(64,158,255,0.55)', width: 1, type: 'solid' }
            },
            backgroundColor: 'rgba(255,255,255,0.98)',
            borderColor: '#e4e7ed',
            borderWidth: 1,
            padding: [10, 12],
            textStyle: { color: '#303133', fontSize: 12 },
            extraCssText:
              'max-width:280px;max-height:360px;overflow:auto;box-shadow:0 4px 16px rgba(0,0,0,0.12);z-index:9999;',
            formatter(params) {
              const idx = params[0] && params[0].dataIndex
              const p = self.series[idx]
              if (!p) return ''
              const soilColor =
                p.soilState === 'S_PLUS' ? '#8B1515' : p.soilState === 'S' ? '#E6A23C' : '#909399'
              const soilName =
                p.soilState === 'S_PLUS'
                  ? 'S+｜冰点修复'
                  : p.soilState === 'S'
                    ? 'S｜主升土壤'
                    : 'NORMAL｜普通土壤'
              const openText =
                p.nextOpenStatus === 'UNAVAILABLE' || p.nextOpenStatus === 'MISSING'
                  ? '数据异常 / 不可用'
                  : fmtPrem(p.nextOpenPremium)
              const wd = weekdayCn(p.date)
              const dateLine = wd
                ? `${p.date}<span style="margin-left:6px;font-weight:500;color:#909399">${wd}</span>`
                : p.date
              const nodeScore =
                p.greedyNodeScore != null && Number.isFinite(Number(p.greedyNodeScore))
                  ? String(Math.round(Number(p.greedyNodeScore)))
                  : '—'
              const nodeHit = isNodeHit(p)
              const nodeLine = nodeHit
                ? `节点分 V5F&nbsp;&nbsp;<b style="color:#c45606;font-size:14px">${nodeScore}</b> · <span style="color:#c45606">达标 ★</span>`
                : `节点分 V5F&nbsp;&nbsp;<b style="font-size:14px">${nodeScore}</b> · <span style="color:#909399">未达标</span>`
              const tops = (Array.isArray(p.firstBoardTop) ? p.firstBoardTop : []).filter(
                (s) => s && s.finalBoards != null && Number(s.finalBoards) >= 3
              )
              const ge3Total =
                p.firstBoardGe3Total != null && Number.isFinite(Number(p.firstBoardGe3Total))
                  ? Number(p.firstBoardGe3Total)
                  : tops.length
              let firstBoardInner = ''
              if (!tops.length) {
                firstBoardInner =
                  '<div style="color:#c0c4cc;font-size:11px">终局≥3板暂无</div>'
              } else {
                const rows = tops
                  .map((s) => {
                    const name = escapeHtml(s.name || s.code || '—')
                    const fb = Math.round(Number(s.finalBoards))
                    return (
                      `<div style="display:flex;justify-content:space-between;gap:12px;font-size:11px;line-height:1.55">` +
                      `<span style="color:#606266">${name}</span>` +
                      `<span style="color:#c45606;font-weight:600;white-space:nowrap">★${fb}</span>` +
                      `</div>`
                    )
                  })
                  .join('')
                const more =
                  ge3Total > tops.length
                    ? `<div style="color:#c0c4cc;font-size:11px;margin-top:2px">另有 ${ge3Total - tops.length} 只终局≥3板</div>`
                    : ''
                firstBoardInner = rows + more
              }
              return (
                `<div style="min-width:168px">` +
                `<div style="font-weight:600;margin-bottom:6px">${dateLine}</div>` +
                `<div style="line-height:1.65">` +
                `接力生态分&nbsp;&nbsp;<b>${fmtScore(p.ecologyScore)}</b><br/>` +
                `隔夜溢价&nbsp;&nbsp;<b>${openText}</b><br/>` +
                `综合晋级率&nbsp;&nbsp;<b>${pct1(p.compositePromotionRate)}</b>` +
                `</div>` +
                `<div style="margin-top:6px">当前土壤：<b style="color:${soilColor}">${soilName}</b></div>` +
                `<div style="margin-top:4px;padding:4px 0">${nodeLine}</div>` +
                `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #ebeef5">` +
                `<div style="color:#909399;font-size:11px;margin-bottom:2px">首板终局 · 复盘结果（≥3板）</div>` +
                firstBoardInner +
                `</div></div>`
              )
            }
          },
          legend: { show: false },
          xAxis: {
            type: 'category',
            data: dates,
            boundaryGap: false,
            axisLine: { lineStyle: { color: '#e4e7ed' } },
            axisTick: { show: false },
            axisLabel: { show: false },
            axisPointer: { label: { show: false } }
          },
          yAxis: [
            {
              type: 'value',
              min: yMin,
              max: yMax,
              splitNumber: 4,
              axisLabel: { color: '#c0c4cc', fontSize: 10 },
              splitLine: { lineStyle: { color: '#f5f7fa', type: 'dashed' } },
              axisLine: { show: false },
              axisTick: { show: false }
            },
            {
              type: 'value',
              scale: true,
              splitNumber: 4,
              axisLabel: {
                color: '#c0c4cc',
                fontSize: 10,
                formatter: (v) => `${v}%`
              },
              splitLine: { show: false },
              axisLine: { show: false },
              axisTick: { show: false }
            }
          ],
          series: [
            this.soilBandSeries(yMax),
            {
              name: '接力生态分',
              type: 'line',
              data: scores,
              smooth: 0.18,
              symbol: 'circle',
              symbolSize: 5,
              z: 3,
              itemStyle: { color: '#409EFF' },
              lineStyle: { width: 2.8, color: '#409EFF' }
            },
            {
              name: '隔夜溢价',
              type: 'line',
              yAxisIndex: 1,
              data: premiums,
              connectNulls: false,
              smooth: 0.18,
              symbol: 'circle',
              symbolSize: 3.5,
              z: 2,
              itemStyle: { color: '#67C23A' },
              lineStyle: { width: 1.4, type: 'dashed', color: 'rgba(103,194,58,0.85)' }
            },
            {
              name: '综合晋级率',
              type: 'line',
              yAxisIndex: 1,
              data: promos,
              smooth: 0.18,
              symbol: 'circle',
              symbolSize: 3.5,
              z: 2,
              itemStyle: { color: '#E6A23C' },
              lineStyle: { width: 1.35, type: 'dotted', color: 'rgba(230,162,60,0.8)' }
            },
            {
              name: '节点达标',
              type: 'scatter',
              data: nodeStarPlot,
              symbol: 'star',
              symbolSize: 11,
              z: 5,
              label: {
                show: true,
                position: 'top',
                distance: 2,
                fontSize: 10,
                fontWeight: 700,
                color: '#C45606',
                formatter(params) {
                  const pt = self.series[params.dataIndex]
                  if (!pt || !isNodeHit(pt)) return ''
                  const s = Number(pt.greedyNodeScore)
                  return Number.isFinite(s) ? String(Math.round(s)) : ''
                }
              },
              itemStyle: {
                color: '#F5A623',
                borderColor: '#C45606',
                borderWidth: 0.8,
                shadowBlur: 2,
                shadowColor: 'rgba(196,86,6,0.35)'
              },
              emphasis: { scale: 1.15 }
            }
          ]
        },
        true
      )

      this.$nextTick(() => this.syncChartHighlight(this.activeIndex))
    }
  }
}
</script>

<style lang="scss" scoped>
.relay-ecology-panel {
  height: auto;
  min-height: 280px;
  max-width: 100%;
  padding: 10px 12px 8px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.rep-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.rep-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 4px;
  &__help {
    color: #c0c4cc;
    font-size: 12px;
    cursor: help;
  }
}

.rep-subtitle {
  margin-top: 2px;
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
}

.rep-resolved {
  margin-left: 8px;
  color: #409eff;
  font-weight: 600;
  font-size: 11px;
}

.rep-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex-shrink: 0;
}

.rep-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid transparent;
  background: transparent;
  cursor: default;
  &--normal {
    color: #7a8799;
    background: #f3f6f9;
    border-color: #e6ebf0;
  }
  &--s {
    color: #a01818;
    font-weight: 700;
    background: linear-gradient(145deg, #fff1f0 0%, #ffd4d0 55%, #ffc2bc 100%);
    border-color: #f0a8a0;
    cursor: pointer;
  }
  &--splus {
    color: #8b1515;
    font-weight: 700;
    background: linear-gradient(145deg, #fff6d8 0%, #f5e2a0 55%, #ecd07a 100%);
    border-color: #d4b45a;
    cursor: pointer;
  }
}

.rep-error {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 13px;
}

.rep-body {
  display: flex;
  gap: 10px;
  flex: 1;
  min-height: 0;
  align-items: flex-start;
}

.rep-side {
  width: 176px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* 与右侧图表区大致对齐，超长名单不顶开下方排版 */
  max-height: 300px;
  overflow: hidden;
  min-height: 0;
  /* 有次日结果卡时固定高度，保证卡内滚动生效 */
  &:has(.nextday-card) {
    height: 300px;
  }
}

.soil-card {
  border-radius: 10px;
  padding: 8px 10px;
  border: 1px solid #e4e7ed;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  &__title {
    font-size: 12px;
    line-height: 1.35;
    display: flex;
    align-items: center;
    gap: 2px;
  }
  &__suffix {
    font-size: 12px;
    font-weight: 600;
  }
  &__chev {
    margin-left: auto;
    font-size: 12px;
    opacity: 0.55;
  }
  &__date {
    margin-top: 4px;
    font-size: 11px;
    opacity: 0.85;
  }
  &__weekday {
    margin-left: 6px;
    font-weight: 600;
    opacity: 0.95;
  }
  &__desc {
    margin-top: 3px;
    font-size: 11px;
    line-height: 1.35;
    opacity: 0.88;
  }
  &__tags {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  &--splus {
    color: #8a6a20;
    border-color: #d4b45a;
    background:
      linear-gradient(125deg, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0) 42%),
      linear-gradient(145deg, #fff6d8 0%, #f0d078 38%, #ffe9a8 62%, #e8c468 100%);
    box-shadow:
      0 2px 10px rgba(184, 140, 40, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.75);
    animation: soil-gold-pulse 2.8s ease-in-out infinite;
    .soil-card__suffix {
      color: #8a6a20;
    }
    .soil-card__date,
    .soil-card__desc {
      color: #7a5a18;
    }
  }
  &--s {
    color: #b42318;
    border-color: #f0a8a0;
    background:
      linear-gradient(125deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 40%),
      linear-gradient(145deg, #fff1f0 0%, #ffd4d0 42%, #ffc2bc 100%);
    box-shadow:
      0 2px 10px rgba(220, 80, 70, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.65);
    animation: soil-red-pulse 2.8s ease-in-out infinite;
    .soil-card__suffix {
      color: #b42318;
    }
    .soil-card__date,
    .soil-card__desc {
      color: #9a2f28;
    }
  }
  &--normal {
    background: #f5f7fa;
    border-color: #e4e7ed;
    color: #606266;
  }
}

.soil-mark {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  &--splus {
    color: #8b1515;
    font-weight: 800;
    font-size: 14px;
    border-radius: 999px;
    padding: 0 5px;
    line-height: 1.35;
    background: rgba(255, 255, 255, 0.55);
    box-shadow: inset 0 0 0 1px rgba(139, 21, 21, 0.12);
  }
  &--s {
    color: #a01818;
    font-weight: 800;
    font-size: 14px;
    border-radius: 999px;
    padding: 0 5px;
    line-height: 1.35;
    background: rgba(255, 255, 255, 0.7);
    box-shadow: inset 0 0 0 1px rgba(160, 24, 24, 0.14);
  }
  &--normal {
    color: #606266;
  }
}

@keyframes soil-gold-pulse {
  0%,
  100% {
    box-shadow:
      0 2px 10px rgba(184, 140, 40, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.7);
    filter: brightness(1);
  }
  50% {
    box-shadow:
      0 4px 14px rgba(212, 168, 48, 0.34),
      inset 0 1px 0 rgba(255, 255, 255, 0.85);
    filter: brightness(1.04);
  }
}

@keyframes soil-red-pulse {
  0%,
  100% {
    box-shadow:
      0 2px 10px rgba(220, 80, 70, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    filter: brightness(1);
  }
  50% {
    box-shadow:
      0 4px 14px rgba(230, 90, 80, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    filter: brightness(1.03);
  }
}

.pill {
  display: inline-flex;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  border: 1px solid #e4e7ed;
  background: rgba(255, 255, 255, 0.75);
  color: inherit;
  &--reward {
    color: #f56c6c;
    border-color: #fbc4c4;
    background: #fff;
  }
  &--support {
    color: #409eff;
    border-color: #b3d8ff;
    background: #fff;
  }
  &--ice {
    color: #f56c6c;
    border-color: #fbc4c4;
    background: #fff;
  }
}

.unlock-btn {
  border: 1px solid #d9ecff;
  background: #ecf5ff;
  color: #409eff;
  border-radius: 6px;
  font-size: 11px;
  padding: 4px 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.rep-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.rep-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 2px;
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
}

.rep-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.lg {
  display: inline-block;
  width: 12px;
  height: 2px;
  border-radius: 1px;
  &--score {
    background: #409eff;
    height: 3px;
  }
  &--prem {
    background: transparent;
    border-top: 2px dashed rgba(103, 194, 58, 0.85);
    height: 0;
  }
  &--promo {
    background: transparent;
    border-top: 2px dotted rgba(230, 162, 60, 0.8);
    height: 0;
  }
  &--node {
    width: 10px;
    height: 10px;
    background: #f5a623;
    border-radius: 0;
    clip-path: polygon(
      50% 0%,
      61% 35%,
      98% 35%,
      68% 57%,
      79% 91%,
      50% 70%,
      21% 91%,
      32% 57%,
      2% 35%,
      39% 35%
    );
  }
}

.rep-chart {
  width: 100%;
  height: 152px;
}

.soil-track {
  position: relative;
  margin-top: 2px;
  min-height: 44px;
  box-sizing: border-box;
  width: 100%;
  &__line {
    position: absolute;
    left: 36px;
    right: 36px;
    top: 8px;
    height: 1px;
    background: #eef1f5;
    pointer-events: none;
  }
  &__cell {
    position: absolute;
    top: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    z-index: 1;
    padding-top: 2px;
    box-sizing: border-box;
    /* 与 boundaryGap:false 一致：首点贴左、末点贴右、中间点居中 */
    &.is-first {
      align-items: flex-start;
    }
    &.is-last {
      align-items: flex-end;
    }
  }
  &__date {
    margin-top: 9px;
    font-size: 10px;
    color: #c0c4cc;
    white-space: nowrap;
    line-height: 1.2;
    &.is-stagger {
      margin-top: 22px;
    }
    &.is-active {
      color: #409eff;
      font-weight: 700;
    }
    &.is-resolved {
      color: #409eff;
    }
  }
}

.soil-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d5e3f0;
  border: 1px solid #fff;
  box-shadow: 0 0 0 1px #e8eef5;
  font-size: 0;
  line-height: 1;
  transition: transform 0.15s, box-shadow 0.15s;
  &--normal {
    background: #d5e3f0;
  }
  &--s {
    width: auto;
    height: auto;
    min-width: 14px;
    padding: 1px 4px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    color: #a01818;
    background: linear-gradient(145deg, #fff1f0 0%, #ffd0cb 55%, #ffbfb8 100%);
    border: 1px solid #f0a8a0;
    box-shadow: 0 1px 3px rgba(220, 80, 70, 0.22);
  }
  &--splus {
    width: auto;
    height: auto;
    min-width: 16px;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    color: #8b1515;
    letter-spacing: 0.02em;
    border: 1px solid #d4b45a;
    background: linear-gradient(145deg, #fff6d8 0%, #f0d078 45%, #e8c468 100%);
    box-shadow: 0 1px 3px rgba(184, 140, 40, 0.28);
  }
  &.is-active {
    transform: scale(1.12);
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.16);
  }
  &--s.is-active {
    box-shadow: 0 0 0 3px rgba(220, 80, 70, 0.28);
  }
  &--splus.is-active {
    box-shadow: 0 0 0 3px rgba(212, 168, 48, 0.35);
  }
}

.rhythm-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f2f5;
}

.rhythm-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #5b7aa0;
  padding: 5px 10px;
  border-radius: 8px;
  background: #eef5fc;
  border: 1px solid #d9e8f7;
  i {
    color: #4a90e2;
    font-size: 14px;
  }
  &__label {
    color: #5b7aa0;
    font-weight: 500;
  }
  &__value {
    font-weight: 700;
    color: #1f2d3d;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.rhythm-s {
  font-style: normal;
  color: #c48a1a;
  font-weight: 800;
}

.rhythm-splus {
  font-style: normal;
  color: #e53935;
  font-weight: 800;
}

.rhythm-link {
  margin-left: auto;
  border: 1px solid #d9e8f7;
  background: #eef5fc;
  color: #4a90e2;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  i {
    font-size: 14px;
  }
  &:hover {
    color: #66b1ff;
    border-color: #c6e2ff;
  }
}

.nextday-card {
  margin-top: 2px;
  padding: 8px 8px 6px;
  border-radius: 10px;
  background: #fafbfd;
  border: 1px solid #e8eef5;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &__head {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 700;
    color: #303133;
    flex-shrink: 0;
    line-height: 1.3;
  }
  &__help {
    color: #909399;
    cursor: help;
    font-size: 13px;
    flex-shrink: 0;
  }
  &__src {
    margin-top: 4px;
    font-size: 10px;
    color: #606266;
    line-height: 1.35;
    flex-shrink: 0;
  }
  &__scroll {
    margin-top: 6px;
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 2px;
  }
  &__loading,
  &__error {
    font-size: 11px;
    color: #909399;
  }
  &__error {
    color: #f56c6c;
  }
}

.nextday-hint {
  margin-top: 4px;
  font-size: 11px;
  color: #909399;
  flex-shrink: 0;
}

.nextday-stage {
  & + & {
    margin-top: 8px;
  }
  &__title {
    font-size: 11px;
    font-weight: 700;
    color: #303133;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px;
  }
  &__counts {
    font-size: 10px;
    font-weight: 600;
    color: #606266;
  }
}

.nextday-winners {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.nextday-winner {
  display: flex;
  align-items: baseline;
  gap: 4px;
  border: 1px solid #f0d0d0;
  border-radius: 5px;
  background: linear-gradient(180deg, #fff8f8 0%, #fff 100%);
  padding: 3px 6px;
  cursor: default;
  font-size: 11px;
  line-height: 1.35;
  &__name {
    font-weight: 700;
    color: #303133;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__code {
    font-size: 10px;
    color: #909399;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
  &__star {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 800;
    color: #c45656;
  }
}

.nextday-empty {
  margin-top: 2px;
  font-size: 10px;
  color: #909399;
}
</style>

<style lang="scss">
/* popover 挂到 body，需非 scoped */
.relay-rhythm-hist-popper {
  padding: 8px !important;
  min-width: 200px;
  max-width: 240px;
}
.rhythm-hist-pop {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rhythm-hist-pop .hist-card {
  border-radius: 8px;
  padding: 7px 9px;
  border: 1px solid #eef1f5;
  background: #f7f9fb;
  color: #606266;
}
.rhythm-hist-pop .hist-card__head {
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  margin-bottom: 5px;
}
.rhythm-hist-pop .hist-card__metrics {
  display: flex;
  gap: 6px;
}
.rhythm-hist-pop .hist-card__metrics--single .hist-metric {
  flex: 0 0 auto;
  min-width: 56px;
}
.rhythm-hist-pop .hist-card__extra {
  margin-top: 2px;
  font-size: 10px;
  color: #a0a4aa;
  line-height: 1.3;
}
.rhythm-hist-pop .hist-metric {
  flex: 1;
  min-width: 0;
}
.rhythm-hist-pop .hist-metric__label {
  display: block;
  font-size: 10px;
  color: #a8abb2;
  line-height: 1.2;
}
.rhythm-hist-pop .hist-metric__val {
  display: block;
  margin-top: 1px;
  font-size: 12px;
  font-weight: 700;
  color: #606266;
}
.rhythm-hist-pop .hist-metric__val--lift {
  color: #f56c6c;
}
.rhythm-hist-pop .aux-box {
  border-radius: 7px;
  border: 1px solid #eef1f5;
  background: #fafbfc;
  padding: 5px 8px;
}
.rhythm-hist-pop .aux-box__head {
  font-size: 11px;
  color: #909399;
  margin-bottom: 3px;
}
.rhythm-hist-pop .aux-box__row {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #8a9bb0;
}
.rhythm-hist-pop .aux-box__row i {
  font-size: 12px;
  color: #a8b8c8;
}
.rhythm-hist-pop .aux-box__label {
  flex: 1;
}
.rhythm-hist-pop .aux-box__val {
  font-weight: 600;
  color: #7a8fa6;
}
</style>
